import React, { useState, useEffect } from 'react';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import {
    NoContent,
    PasslyInfiniteScroll,
    PageLoader,
    IModalData,
    ModalComponent,
    PasslyButton,
    IMenuItem,
    mainMenuTypes,
    mainMenuTop, PasslyTextField
} from '@app/components';
import { setExceptionHandler, getModalSwitch } from '@app/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { vaultsService, basicService, passwordServerService } from '@app/services';
import { IListPageBarEnabled, IActiveFilter, IVaultsDisplay, OrderType, Order, VaultSearchItem } from '@app/models';
import { infiniteScrollItemsFetchLimit, useDebounce, genericErrorToast, getMenuItemById } from '@app/helpers';
import VpnLockIcon from '@material-ui/icons/VpnLock';
import ScreenLockPortraitIcon from '@material-ui/icons/ScreenLockPortrait';
import { AddIndividualVaultDialog } from '@app/pages/password/dialogs/addIndividualVault.dialog';
import { PasslyTooltip } from '@app/componentsV2';
import { SearchResult } from '@app/components/SearchResult';

interface IVaultsTableData {
    id: string;
    cutId: string;
    displayName: string;
    vaultType: string;
    passwordCount: number;
    folderCount: number;
}

const PasswordVaultPage = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const activeFilters: IActiveFilter[] = useSelector((state: any) => state.listPageBar.activeFilters);
    const organizationFilter = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const currentUserOrgId = useSelector((state: any) => state.signIn.currentUser.oid as string);
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.PasswordVaults, mainMenuTop);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [rows, setRows] = useState<IVaultsTableData[]>([]);
    const [searchResults, setSearchResults] = useState<VaultSearchItem[]>([]);
    const [vaultCount, setVaultCount] = useState(0);
    const [searchCount, setSearchCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasIndividualVault, setHasIndividualVault] = useState(true);
    const [searchField, setSearchField] = useState('');
    const [orgCanView, setOrgCanView] = useState<boolean>(true);
    const [hasDoneASearch, setHasDoneASearch] = useState<boolean>(false);
    const [resetScrollCount, setResetScrollCount] = useState<number>(0);
    const [resetSearchScrollCount, setResetSearchScrollCount] = useState<number>(0);

    const debouncedSearch = useDebounce(searchField.trim(), 500);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: false,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    useSetPage(setListPageBarEnabledOptions, item);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        vaultsService.hasIndividualVault(handleError, handleSuccessIndividualVault);
    }, []);

    useEffect(() => {
        setOrgCanView(true);
        setRows([]);
        clearSearch();
    }, [organizationFilter]);

    useEffect(() => {
        if (debouncedSearch !== '') {
            setHasDoneASearch(false);
            setResetSearchScrollCount(resetSearchScrollCount + 1);
        } else if (debouncedSearch === '' && hasDoneASearch) {
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [debouncedSearch]);

    const clearSearch = () => {
        setSearchResults([]);
        setHasDoneASearch(false);
        setSearchField('');
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        setIsLoading(true);
        vaultsService.getVaults(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            organizationFilter,
            handleError,
            handleSuccess,
            isReset,
            activeFilters
        );
    };

    const fetchMoreSearchedData = (skipCount: number, searchString: string, isReset: boolean) => {
        if (!isLoading) {
            setIsLoading(true);
            passwordServerService.doVaultComplexSearch(
                searchString,
                skipCount,
                infiniteScrollItemsFetchLimit,
                handleErrorShow,
                handleSuccessNewSearch(isReset)
            );
        }
    };

    const handleSuccessNewSearch = (isReset: boolean) => (response: any) => {
        const results: VaultSearchItem[] = response.data.items;
        const count: number = response.data.count;

        setIsLoading(false);
        setSearchCount(count);

        if (results.length === 0) {
            setSearchResults([]);
        } else if (isReset) {
            setSearchResults(results);
        } else {
            setSearchResults((previousState: any) => previousState.concat(results));
        }

        setHasDoneASearch(true);
    };

    const handleSuccessIndividualVault = (response: any) => {
        const result: boolean = response.data.value;

        setHasIndividualVault(result);
    };

    const handleSuccess = (response: any) => {
        const results: IVaultsDisplay[] = response.data.value;
        setOrgCanView(true);
        clearSearch();

        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setVaultCount(count);
        }

        if (results) {
            let newRows = results.map((row: IVaultsDisplay) => {
                const data: IVaultsTableData = {
                    id: row.Id,
                    cutId: `...${row.Id.slice(-12)}`,
                    displayName: row.DisplayName,
                    vaultType: row.VaultType,
                    passwordCount: row.PasswordCount,
                    folderCount: row.FolderCount
                };
                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setIsLoading(false);
    };

    const handleError = (error: any) => {
        const respStatus = error.response.status;
        const errorMessage = error.response.data.error.message;

        setVaultCount(0);
        setIsLoading(false);

        if (respStatus === 400 && errorMessage === 'ORG_CANNOT_VIEW') {
            setOrgCanView(false);
            return;
        }

        setOrgCanView(true);
        handleErrorShow(error.response);
    };

    const handleErrorShow = (errorResponse: any) => {
        setIsLoading(false);
        genericErrorToast(errorResponse);
    };

    const openAddIndividualModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('NEW_INDIVIDUAL_VAULT'),
            modalSwitcher: modalSwitchState,
            form: <AddIndividualVaultDialog handleRefresh={handlePageRefreshFromDialog} />
        });
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean, fromIndividualVault: boolean) => {
        if (refreshPage) {
            clearSearch();
            setResetScrollCount(resetScrollCount + 1);
        }

        if (fromIndividualVault) {
            setHasIndividualVault(true);
        }
    };

    const handleRedirect = (id: string) => {
        let url: string = `/passwords/details/${id}`;

        basicService.redirect(url);
    };

    return (
        <>
            <ModalComponent {...modalData} />
            {orgCanView && (
                <div className='passwordVaults'>
                    <div className='vaults__section-head-inputs'>
                        <PasslyTextField
                            name='search'
                            label=''
                            value={searchField}
                            onChange={(e) => setSearchField(e.target.value)}
                            id='search-input'
                            inputProps={{ maxLength: 255 }}
                            placeholder={translation('SEARCH')}
                            showSearchIcon={true}
                            autoFocus />

                        {!hasIndividualVault && organizationFilter === currentUserOrgId && (
                            <PasslyButton variant='contained' color='primary' id='addIndividualVault' onClick={openAddIndividualModal}>
                                + {translation('NEW_INDIVIDUAL_VAULT')}
                            </PasslyButton>
                        )}
                    </div>
                </div>
            )}
            {orgCanView && (
                <>
                    {!debouncedSearch ? (
                        <PasslyInfiniteScroll
                            totalCount={vaultCount}
                            currentCount={rows.length}
                            onFetch={fetchMoreData}
                            resetLocal={resetScrollCount}
                        >
                            <>
                                {rows && rows.length > 0 ? (
                                    <div className='passwordVaults'>
                                        <div className='passwordVaults__column'>
                                            {rows.map((row, index: number) => (
                                                <div key={index} className='passwordVaults__column-tile passwordVaults__column-tile--vaults'>
                                                    <a id='clickOnVault' onClick={() => handleRedirect(row.id)}>
                                                        {row.vaultType === 'Individual' ? (
                                                            <span>
                                                                <ScreenLockPortraitIcon color='primary' fontSize='large' />
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                <VpnLockIcon color='primary' fontSize='large' />
                                                            </span>
                                                        )}
                                                        <span className='passwordVaults__vaultTile'>
                                                            <span className='passwordVaults__vaultTitle--subtitle'>
                                                                {row.displayName} (
                                                                <PasslyTooltip title={row.id}>
                                                                    <span>{row.cutId}</span>
                                                                </PasslyTooltip>
                                                                )
                                                            </span>
                                                            <span className='passwordVaults__vaultDetail'>
                                                                <span className='passwordVaults__vaultDetail--detail'>
                                                                    {translation('VAULT_FOLDER_TAB')}: {row.folderCount}
                                                                </span>
                                                                <span className='passwordVaults__vaultDetail--detail'>
                                                                    {translation('VAULT_PASSWORDS_TAB')}: {row.passwordCount}
                                                                </span>
                                                            </span>
                                                        </span>
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </>
                        </PasslyInfiniteScroll>
                    ) : (
                        <SearchResult
                            rows={searchResults}
                            totalCount={searchCount}
                            currentCount={searchResults.length}
                            onFetch={fetchMoreSearchedData}
                            resetLocal={resetSearchScrollCount}
                            searchString={debouncedSearch}
                            hasDoneASearch={hasDoneASearch}
                        />
                    )}
                </>
            )}

            {orgCanView && !isLoading && !hasDoneASearch && (rows === undefined || rows.length === 0) && (
                <NoContent header={'READY_FOR_PASSWORD_VAULTS'} text={'READY_FOR_PASSWORD_VAULTS_BODY'} />
            )}

            {!orgCanView && !isLoading && <div className='passwordVaults__no-results'> {translation('ORG_CANNOT_VIEW')}</div>}

            {isLoading && <PageLoader />}
        </>
    );
};

export default PasswordVaultPage;
